import React, { useRef, useState } from 'react';
import { string, bool, func } from 'prop-types';
import { compose } from 'redux';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { Form as FinalForm } from 'react-final-form';
import { propTypes } from '../../util/types';
import { FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import {
    Form,
    FieldTextInput,
    AppointmentScheduler,
    Button,
    Controls,
    Tooltip,
    MissingPhoneNumberModal,
} from '../../components';
import {
    IconCalendarL,
    IconCrossL,
    IconPaperclipL,
    IconPhoneL,
    IconPlusStrongM,
} from '../../icons';
import css from './SendMessageForm.css';
import { checkIOS } from '../../util/ios';
import { useDispatch, useSelector } from 'react-redux';
import { toggleUiComponentBooleanState } from '../../ducks/UI.duck';
import { useHandleKeyDown } from './useHandleKeyDown';
import { ConditionalWrapper } from '../../util/common';
import { AppointmentSchedulerContext } from '../../components/AppointmentScheduler/AppointmentScheduler';
import { usePendingAppointment } from '../../components/AppointmentScheduler/usePendingAppointment';
import { ENTER_PHONE_NUMBER } from '../../components/MissingPhoneNumberModal/helpers';
import ContactPreferenceModal from '../ContactDetailsForm/ContactPreferenceModal';
import getContactInfoMessageBody from './util/getContactInfoMessageBody';
import {
    getNewSessionTooltipVisibility,
    setNewSessionTooltipVisibility,
} from '../../util/localStorage';
import { useTransactionPanelContext } from '../../components/TransactionPanel/TransactionPanelContext';

const BLUR_TIMEOUT_MS = 100;
export const ACCEPT_ATTR =
    'image/*,.doc,.docx,.xml,.pdf,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const SendMessageForm = ({
    onFocus,
    onBlur,
    rootClassName,
    className,
    messagePlaceholder,
    inProgress,
    sendMessageError,
    formId,
    onDrag: onDragHandler,
    onDrop: onDropHandler,
    onImageUploadHandler,
    imageUploadRequested,
    sendFileInProgress,
    sendMessageInProgress,
    isMobile,
    ...rest
}) => {
    const { formError } = useTransactionPanelContext();

    const [blurTimeoutId, setBlurTimeoutId] = useState(null);
    const [focused, setFocused] = useState(false);
    const [controlsPanelOpen, setControlsPanelOpen] = useState(false);
    const [newSessionTooltipVisible, setNewSessionTooltipVisible] = useState(
        getNewSessionTooltipVisibility()
    );
    const [missingPhoneNumModalVisible, setMissingPhoneNumModalVisibility] = useState(false);
    const [missingPhoneNumRefreshKey, setMissingPhoneNumRefreshKey] = useState(
        new Date().getTime()
    );
    const [appointmentSchedulerModalVisible, setAppointmentSchedulerModalVisibility] = useState(
        false
    );
    const [isContactPreferenceModalOpen, setIsContactPreferenceModalOpen] = useState(false);

    const messageInput = useRef();
    const submitOnKeyPressAllowed = useRef();
    const addImageRef = useRef(null);

    const dispatch = useDispatch();
    const isBottomMobileBarVisible = useSelector(s => s.UI['bottomMobileBarVisible']);
    const currentUser = useSelector(state => state.user.currentUser);

    const toggleBottomMobileBarVisibility = fieldState =>
        dispatch(toggleUiComponentBooleanState(['bottomMobileBarVisible', fieldState]));

    const handleNewSessionTooltipVisibility = visibility => {
        setNewSessionTooltipVisible(visibility);
        setNewSessionTooltipVisibility(visibility);
    };

    const handleFormFocus = () => {
        submitOnKeyPressAllowed.current = true;

        onFocus();
        window.clearTimeout(blurTimeoutId);
        const { iOS, webView, isNative } = checkIOS();
        toggleBottomMobileBarVisibility(false);
        /**
         * If the user is on a native NON iOS app,
         * the native keyboard overlaps the send message element;
         *
         * the setFocused adds a css class that increases the bottom spacing
         */
        if ((webView || isNative) && !iOS) {
            setFocused(true);
            setTimeout(() => {
                window.scrollTo(0, document.body.scrollHeight);
            }, 500);
        }
    };

    const handleFormBlur = () => {
        submitOnKeyPressAllowed.current = false;

        setBlurTimeoutId(
            window.setTimeout(() => {
                toggleBottomMobileBarVisibility(true);
                setFocused(false);
                onBlur();
            }, BLUR_TIMEOUT_MS)
        );
    };

    const handleControlsVisibility = fieldState => {
        setControlsPanelOpen(fieldState);
        handleNewSessionTooltipVisibility(false);
    };

    const handleMobileNumberSharing = async () => {
        const { attributes } = currentUser || {};
        const {
            profile: { publicData, protectedData },
        } = attributes || {};

        const currentPhoneNumberVerified = publicData?.phoneVerified;
        const currentPhoneNumber = protectedData?.phoneNumber;

        if (!currentPhoneNumber || !currentPhoneNumberVerified) {
            return setMissingPhoneNumModalVisibility(true);
        }

        await rest.onSubmit({ message: getContactInfoMessageBody({ currentUser }) });
    };

    useHandleKeyDown({
        isMobile,
        messageInput,
        submitOnKeyPressAllowed,
        onSubmit: rest.onSubmit,
    });

    const { hasPendingAppointment } = usePendingAppointment({
        appointmentEntities: rest.appointmentEntities,
        transactionId: rest.transaction?.id?.uuid,
    });

    return (
        <>
            {missingPhoneNumModalVisible && (
                <MissingPhoneNumberModal
                    isOpen
                    id="TransactionPage.missingPhoneNumberModal"
                    initialState={ENTER_PHONE_NUMBER}
                    key={missingPhoneNumRefreshKey}
                    onClose={() => {
                        setMissingPhoneNumModalVisibility(false);
                        setMissingPhoneNumRefreshKey(new Date().getTime());
                    }}
                />
            )}
            {isContactPreferenceModalOpen && (
                <ContactPreferenceModal
                    isOpen={isContactPreferenceModalOpen}
                    onClose={() => setIsContactPreferenceModalOpen(false)}
                />
            )}

            <FinalForm
                {...rest}
                render={formRenderProps => {
                    const { handleSubmit, values, form } = formRenderProps;

                    const classes = classNames(rootClassName || css.root, className, {
                        [css.focused]: focused,
                        [css.noExtraBottomSpacing]: !isBottomMobileBarVisible,
                    });

                    const { message } = values;
                    const sendImageDisabled = sendFileInProgress || sendMessageInProgress;
                    const sendMessageDisabled =
                        imageUploadRequested || sendMessageInProgress || !message;

                    return (
                        <Form
                            onSubmit={values => handleSubmit(values, form)}
                            onDragOver={e => onDragHandler(e, true)}
                            onDragEnter={e => onDragHandler(e, true)}
                            onDragLeave={e => onDragHandler(e, false)}
                            onDragEnd={e => onDragHandler(e, false)}
                            onDrop={e => onDropHandler(e, form)}
                            id="sendMessageForm"
                            className={classes}
                        >
                            <FieldTextInput
                                inputRootClass={css.textarea}
                                className={css.sendMessageFormInput}
                                type="textarea"
                                id={formId ? `${formId}.message` : 'message'}
                                name="message"
                                placeholder={messagePlaceholder}
                                onFocus={handleFormFocus}
                                onBlur={handleFormBlur}
                                inputRef={messageInput}
                                disabled={imageUploadRequested}
                            />
                            <div>
                                <AppointmentSchedulerContext.Provider
                                    value={{
                                        hasPendingAppointment,
                                        appointmentSchedulerModalVisible,
                                        setAppointmentSchedulerModalVisibility,
                                    }}
                                >
                                    <section className={css.outerContainer}>
                                        <ConditionalWrapper
                                            condition={newSessionTooltipVisible}
                                            wrapper={children => (
                                                <Tooltip
                                                    position="top"
                                                    isStatic
                                                    staticTooltipState={newSessionTooltipVisible}
                                                    tooltipClassName={css.newSessionTooltip}
                                                    content={
                                                        <>
                                                            <IconCrossL
                                                                rootClassName={
                                                                    css.newSessionTooltipCloseButton
                                                                }
                                                                clickHandler={() =>
                                                                    handleNewSessionTooltipVisibility(
                                                                        false
                                                                    )
                                                                }
                                                            />
                                                            <p className={css.newSessionHeading}>
                                                                <span>Mobilnummer senden</span>
                                                                <span
                                                                    className={css.newSessionBadge}
                                                                >
                                                                    Neu
                                                                </span>
                                                            </p>
                                                            <p>
                                                                Teile deine Mobilnummer mit einem
                                                                Klick.
                                                            </p>
                                                        </>
                                                    }
                                                >
                                                    {children}
                                                </Tooltip>
                                            )}
                                        >
                                            <AppointmentScheduler {...rest} />

                                            <div className={css.controlsHolder}>
                                                <Controls
                                                    tooltipClassName={css.controlList}
                                                    isOpened={controlsPanelOpen}
                                                    handleToggle={() =>
                                                        handleControlsVisibility(!controlsPanelOpen)
                                                    }
                                                    controlHolderProps={{
                                                        outsideClickHandler: () =>
                                                            handleControlsVisibility(false),
                                                    }}
                                                    menu={
                                                        <Controls.Menu>
                                                            <Controls.Item
                                                                icon={<IconPaperclipL />}
                                                                onClick={e => {
                                                                    e.stopPropagation();
                                                                    void addImageRef.current?.click();
                                                                }}
                                                            >
                                                                Datei
                                                            </Controls.Item>
                                                            <Controls.Item
                                                                icon={<IconPhoneL />}
                                                                onClick={() =>
                                                                    handleMobileNumberSharing()
                                                                }
                                                            >
                                                                Nummer
                                                            </Controls.Item>
                                                            <Controls.Item
                                                                icon={<IconCalendarL />}
                                                                className={classNames({
                                                                    [css.disabled]: hasPendingAppointment,
                                                                })}
                                                                onClick={() =>
                                                                    !hasPendingAppointment &&
                                                                    setAppointmentSchedulerModalVisibility(
                                                                        true
                                                                    )
                                                                }
                                                            >
                                                                Termin
                                                            </Controls.Item>
                                                        </Controls.Menu>
                                                    }
                                                >
                                                    <Controls.Trigger
                                                        className={classNames(css.controlsTrigger, {
                                                            [css.controlListOpened]: controlsPanelOpen,
                                                        })}
                                                    >
                                                        <IconPlusStrongM />
                                                    </Controls.Trigger>
                                                </Controls>
                                            </div>
                                        </ConditionalWrapper>

                                        {formError && (
                                            <div className={css.errorContainer}>
                                                <p className={css.error}>{formError}</p>
                                            </div>
                                        )}

                                        {sendMessageError && (
                                            <div className={css.errorContainer}>
                                                <p className={css.error}>
                                                    <FormattedMessage id="SendMessageForm.sendFailed" />
                                                </p>
                                            </div>
                                        )}

                                        <Button
                                            className={classNames([
                                                css.submitButton,
                                                css.sendMessageButton,
                                            ])}
                                            inProgress={sendMessageInProgress}
                                            disabled={sendMessageDisabled}
                                            onBlur={handleFormBlur}
                                        >
                                            <FormattedMessage id="SendMessageForm.sendMessage" />
                                        </Button>
                                        <input
                                            id="addImage"
                                            name="addImage"
                                            type="file"
                                            ref={addImageRef}
                                            className={css.attachFileInput}
                                            disabled={sendImageDisabled}
                                            accept={ACCEPT_ATTR}
                                            onChange={e => {
                                                const files = e.target.files;
                                                onImageUploadHandler(files, form);
                                            }}
                                        />
                                    </section>{' '}
                                </AppointmentSchedulerContext.Provider>
                            </div>
                        </Form>
                    );
                }}
            />
        </>
    );
};

SendMessageForm.defaultProps = {
    rootClassName: null,
    className: null,
    inProgress: false,
    messagePlaceholder: null,
    onFocus: () => null,
    onBlur: () => null,
    onDragOver: () => null,
    onDragEnter: () => null,
    onDragLeave: () => null,
    onDragEnd: () => null,
    onDrop: () => null,
    sendMessageError: null,
    imageUploadRequested: false,
};

SendMessageForm.propTypes = {
    rootClassName: string,
    className: string,
    inProgress: bool,
    imageUploadRequested: bool,
    messagePlaceholder: string,
    onSubmit: func.isRequired,
    onFocus: func,
    onBlur: func,
    onDragOver: func,
    onDragEnter: func,
    onDragLeave: func,
    onDragEnd: func,
    onDrop: func,
    sendMessageError: propTypes.error,

    // from injectIntl
    intl: intlShape.isRequired,
};

export default compose(injectIntl)(SendMessageForm);
